import { useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { ExternalLink, LogOut } from 'lucide-react';
import type { Session } from '@supabase/supabase-js';

import { useBrandQuery, useMeQuery, queryKeys } from '../lib/queries';
import { detectInitialLang } from '../lib/i18n';
import { subscribeToConfig } from '../lib/realtime';
import { getSupabase } from '../lib/supabase';
import { useAppStore } from '../lib/store';
import { applyTheme } from '../lib/themes';
import { t } from '../lib/translations';

import LangSwitch from '../components/LangSwitch';
import LoginForm from '../components/admin/LoginForm';
import BrandSwitch from '../components/admin/BrandSwitch';
import LlmChainConfig from '../components/admin/LlmChainConfig';
import ProductList from '../components/admin/ProductList';
import QuickScenariosPanel from '../components/admin/QuickScenariosPanel';
import AdminUsersPanel from '../components/admin/AdminUsersPanel';

/**
 * /admin — Google OAuth gate (design §8.2 post-upgrade).
 * Flow:
 *   no session              → LoginForm
 *   session, /me = 403      → NotWhitelistedPanel (with sign-out)
 *   session, /me OK         → admin UI (role-gated panels in Stage 7)
 */
export default function AdminPage() {
  const lang = useAppStore((s) => s.lang);
  const brand = useAppStore((s) => s.brand);
  const setLang = useAppStore((s) => s.setLang);
  const setBrand = useAppStore((s) => s.setBrand);
  const ui = t(lang);

  const qc = useQueryClient();
  const brandQuery = useBrandQuery();

  // Supabase session state — undefined while we check, null when signed out,
  // a Session object when authenticated.
  const [session, setSession] = useState<Session | null | undefined>(undefined);
  const me = useMeQuery(!!session);

  useEffect(() => {
    const supabase = getSupabase();
    if (!supabase) {
      setSession(null);
      return;
    }
    supabase.auth.getSession().then(({ data }) => setSession(data.session));
    const { data: sub } = supabase.auth.onAuthStateChange((_event, s) => {
      setSession(s);
      // New session/logout → fetch /me afresh
      qc.invalidateQueries({ queryKey: queryKeys.me });
    });
    return () => sub.subscription.unsubscribe();
  }, [qc]);

  useEffect(() => {
    let cancelled = false;
    detectInitialLang().then((resolved) => {
      if (!cancelled) setLang(resolved);
    });
    return () => {
      cancelled = true;
    };
  }, [setLang]);

  useEffect(() => {
    if (brandQuery.data?.brand && brandQuery.data.brand !== brand) {
      setBrand(brandQuery.data.brand);
    }
  }, [brandQuery.data?.brand, brand, setBrand]);

  useEffect(() => {
    applyTheme(brand);
  }, [brand]);

  useEffect(() => subscribeToConfig(qc), [qc]);

  const signOut = async () => {
    const supabase = getSupabase();
    if (supabase) await supabase.auth.signOut();
    qc.clear();
  };

  const authed = !!me.data;

  return (
    <div className="min-h-dvh bg-[var(--bg-base)] text-[var(--text-primary)]">
      <header className="sticky top-0 z-20 border-b border-slate-200 backdrop-blur bg-[var(--bg-base)]/80">
        <div className="mx-auto max-w-4xl flex items-center justify-between gap-3 px-4 sm:px-6 py-3">
          <div className="flex items-baseline gap-3 min-w-0">
            <h1 className="text-sm sm:text-base font-semibold shrink-0">{ui.adminTitle}</h1>
            {/* Identity strip — fresh editors land in /admin not knowing what
                account they signed in with. Showing email + role badge here
                avoids the "光秃秃" first impression and helps super_admin
                eyeball who's logged in across multiple devices/sessions. */}
            {me.data && (
              <div className="hidden sm:flex items-center gap-1.5 text-xs text-slate-500 truncate">
                <span className="truncate" title={me.data.email}>
                  {me.data.email}
                </span>
                <span
                  className={[
                    'shrink-0 rounded-full px-1.5 py-0.5 text-[10px]',
                    me.data.role === 'super_admin'
                      ? 'bg-amber-500/10 text-amber-200'
                      : 'bg-slate-50 text-slate-500',
                  ].join(' ')}
                >
                  {me.data.role === 'super_admin'
                    ? ui.adminUsersRoleSuperAdmin
                    : ui.adminUsersRoleEditor}
                </span>
              </div>
            )}
          </div>
          <div className="flex items-center gap-3">
            <LangSwitch />
            {/* 预览首页：admin 改完产品后一键看效果。新 tab 打开，保留 admin
                上下文，方便对照着改。只在已登录态显示。 */}
            {(authed || session) && (
              <a
                href="/"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 rounded-full accent-bg text-white px-3 py-1.5 text-xs font-medium hover:brightness-110"
              >
                <ExternalLink size={12} />
                {ui.adminPreviewHome}
              </a>
            )}
            {(authed || session) && (
              <button
                type="button"
                onClick={signOut}
                className="inline-flex items-center gap-1 rounded-full border border-slate-200 bg-slate-50 px-3 py-1.5 text-xs text-slate-700 hover:bg-slate-100"
              >
                <LogOut size={12} />
                {ui.adminLogout}
              </button>
            )}
          </div>
        </div>
        {/* Mobile fallback: identity below the title row when the inline
            version above gets hidden (sm:flex hidden on mobile). */}
        {me.data && (
          <div className="sm:hidden mx-auto max-w-4xl px-4 pb-2 flex items-center gap-1.5 text-xs text-slate-500">
            <span className="truncate" title={me.data.email}>
              {me.data.email}
            </span>
            <span
              className={[
                'shrink-0 rounded-full px-1.5 py-0.5 text-[10px]',
                me.data.role === 'super_admin'
                  ? 'bg-amber-500/10 text-amber-200'
                  : 'bg-slate-50 text-slate-500',
              ].join(' ')}
            >
              {me.data.role === 'super_admin'
                ? ui.adminUsersRoleSuperAdmin
                : ui.adminUsersRoleEditor}
            </span>
          </div>
        )}
      </header>

      <main className="mx-auto max-w-4xl px-4 sm:px-6 py-8">
        {session === undefined || (session && me.isLoading) ? (
          <p className="mt-20 text-center text-sm text-slate-500">…</p>
        ) : !session ? (
          <LoginForm lang={lang} />
        ) : !me.data ? (
          <div className="mx-auto mt-20 w-full max-w-md rounded-2xl border border-amber-400/20 bg-amber-500/5 p-6">
            <h2 className="text-lg font-semibold text-amber-200">{ui.adminNotWhitelistedTitle}</h2>
            <p className="mt-3 text-sm text-slate-700">
              {session.user.email && (
                <span className="font-mono text-slate-900">{session.user.email}</span>
              )}
            </p>
            <p className="mt-3 text-sm text-slate-600">{ui.adminNotWhitelistedHint}</p>
            <button
              type="button"
              onClick={signOut}
              className="mt-6 inline-flex items-center gap-1 rounded-full border border-slate-200 bg-slate-50 px-3 py-1.5 text-xs text-slate-700 hover:bg-slate-100"
            >
              {ui.adminSwitchAccount}
            </button>
          </div>
        ) : (
          <div className="flex flex-col gap-6">
            {me.data.role === 'super_admin' && (
              <>
                <BrandSwitch />
                <LlmChainConfig />
                <QuickScenariosPanel />
                <AdminUsersPanel me={me.data} />
              </>
            )}
            <ProductList me={me.data} />
          </div>
        )}
      </main>
    </div>
  );
}
